/**
 * Shared column configuration for TAS Programs reporting tables
 *
 * This configuration is used by both individual and aggregate table components
 * to ensure consistent column labels, types, and linkable routes across views.
 *
 * **Field Name Format Convention:**
 * - Keys in this config are snake_case (matching OpenSearch field names and query parameters)
 * - Components normalize these to camelCase for internal use (to match API response format)
 * - The snakeToCamelCase utility handles the conversion at component boundaries
 *
 * **Why Share This Config:**
 * - Single source of truth for column configuration
 * - Consistent labels across individual and aggregate views
 * - Easier to add new columns (only update one file)
 * - Type-safe column keys
 * - Reduces code duplication
 */

export type ColumnType =
  | 'text'
  | 'currency'
  | 'date'
  | 'boolean'
  | 'array-primitive'
  | 'array-objects';

/**
 * Configuration for displaying nested object fields in array-objects columns.
 * Used to control which fields are shown from array objects (e.g., courses).
 */
export interface NestedFieldConfig {
  /** Field key in the nested object (can be camelCase or snake_case) */
  readonly field: string;
  /** Optional custom label for the field. Use false to show value only without label. If undefined, auto-generates label. */
  readonly label?: string | false;
  /** Optional type override for the field */
  readonly type?: ColumnType;
}

/**
 * Configuration for array-objects column type.
 * Allows specifying which nested fields to display and how to format them.
 */
export interface ArrayObjectsConfig {
  /** Fields to display from each nested object */
  readonly fields: readonly NestedFieldConfig[];
  /** Separator between field entries (default: '\n') */
  readonly separator?: string;
}

/**
 * Column configuration structure
 */
export interface ColumnConfig {
  label: string;
  type: ColumnType;
  linkable: string | false; // Route pattern or false if not linkable
  /** Configuration for array-objects type - which nested fields to show */
  arrayConfig?: ArrayObjectsConfig;
}

/**
 * Master column configuration used by both individual and aggregate tables.
 *
 * **Keys are in snake_case** (matching OpenSearch field names and query parameters).
 * Components will convert these to camelCase using snakeToCamelCase() for internal use.
 *
 * **When to Add Entries:**
 * Only add entries when you need:
 * 1. Custom routes (linkable !== false)
 * 2. Custom labels that differ from auto-generated ones
 * 3. Array object configurations (arrayConfig for nested fields)
 *
 * All other fields will use auto-generated labels via generateSmartLabel().
 */
export const TAS_PROGRAMS_COLUMN_MAPPING = {
  // ============================================================================
  // PROGRAM FIELDS
  // ============================================================================

  // Note: tas_program_template_field_program_name_keyword intentionally NOT in config
  // to allow generateSmartLabel to create "Program: Name" for disambiguation

  tas_program_instance_id_keyword: {
    label: 'Program Instance ID',
    type: 'text',
    linkable: 'program-instances.show',
  },

  // ============================================================================
  // APPLICATION FIELDS
  // ============================================================================

  tas_application_id_keyword: {
    label: 'Application ID',
    type: 'text',
    linkable: 'applications.show',
  },

  // ============================================================================
  // PEOPLE FIELDS
  // ============================================================================

  'employee_ef_fields.employee_person_id_keyword': {
    label: 'Person ID',
    type: 'text',
    linkable: 'people.show',
  },

  // ============================================================================
  // COMPANY FIELDS
  // ============================================================================

  company_id_keyword: {
    label: 'Company ID',
    type: 'text',
    linkable: 'companies.show',
  },

  company_id: {
    label: 'Company ID',
    type: 'text',
    linkable: 'companies.show',
  },

  // ============================================================================
  // SCHOOL FIELDS
  // ============================================================================

  tas_program_instance_field_school_institution_name_keyword: {
    label: 'School',
    type: 'text',
    linkable: false,
  },

  // ============================================================================
  // EMPLOYEE FIELDS
  // ============================================================================

  employee_date_of_birth: {
    label: 'Employee DOB', // Auto-generated would be "Employee Date Of Birth"
    type: 'date',
    linkable: false,
  },

  // ============================================================================
  // COURSE FIELDS (Array Objects)
  // ============================================================================

  courses: {
    label: 'Courses',
    type: 'array-objects',
    linkable: false,
    arrayConfig: {
      fields: [
        { field: 'tasCourseFieldCourseNameKeyword', label: false }, // false = no label, just value
        // Uncomment below to show additional fields:
        // { field: 'courseId', label: 'Course ID' },
        // { field: 'tasCourseFieldCourseNumberKeyword', label: 'Course Number' },
        // { field: 'tasCourseFieldCourseTuitionInteger', label: 'Tuition', type: 'currency' },
        // { field: 'tasCourseFieldCourseDescriptionKeyword', label: 'Description' },
        // { field: 'tasCourseFieldApprovedCourseTotalInteger', label: 'Approved Total', type: 'currency' },
      ],
      separator: ', ',
    },
  },
} as const;

/**
 * Type helper to extract column keys from the mapping
 */
export type TASColumnKey = keyof typeof TAS_PROGRAMS_COLUMN_MAPPING;

/**
 * Aggregation column definitions (only for aggregate mode).
 * These are computed columns not present in OpenSearch - they're calculated from
 * the applicationStateCounts in the aggregated data.
 */
export const AGGREGATION_COLUMNS = {
  total_applications: {
    label: 'Total Applications',
    type: 'text',
    linkable: false,
  },
  in_draft: {
    label: 'In Draft',
    type: 'text',
    linkable: false,
  },
  in_progress: {
    label: 'In Progress',
    type: 'text',
    linkable: false,
  },
  completed: {
    label: 'Completed',
    type: 'text',
    linkable: false,
  },
} as const;

/**
 * Type helper for aggregation column keys
 */
export type AggregationColumnKey = keyof typeof AGGREGATION_COLUMNS;

/**
 * Default column selections for different views.
 * These define what columns show by default when no explicit selection is provided.
 *
 * **Keys are in snake_case** to match OpenSearch field names.
 */
export const DEFAULT_COLUMNS = {
  /**
   * Default columns for individual application view.
   * Shows detailed information for each individual application.
   */
  individual: [
    'employee_name_keyword',
    'employee_payroll_id_keyword',
    'tas_program_template_field_program_name_keyword',
    'tas_program_instance_field_school_institution_name_keyword',
    'tas_application_id_keyword',
    'tas_program_instance_created_at_date',
  ],

  /**
   * Default columns for aggregated view (grouped by program instance).
   * Shows summary information with application counts.
   */
  aggregate: [
    'employee_name_keyword',
    'employee_payroll_id_keyword',
    'tas_program_template_field_program_name_keyword',
    'tas_program_instance_field_school_institution_name_keyword',
    'tas_program_instance_id_keyword',
    'total_applications',
    'in_draft',
    'in_progress',
    'completed',
  ],
} as const;

/**
 * Helper function to check if a column key exists in the shared mapping
 */
export function hasColumnConfig(columnKey: string): columnKey is TASColumnKey {
  return columnKey in TAS_PROGRAMS_COLUMN_MAPPING;
}

/**
 * Helper function to check if a column key is an aggregation column
 */
export function isAggregationColumn(columnKey: string): columnKey is AggregationColumnKey {
  return columnKey in AGGREGATION_COLUMNS;
}

/**
 * Helper function to get column configuration safely
 * Returns the config if it exists, undefined otherwise
 */
export function getColumnConfig(columnKey: string): ColumnConfig | undefined {
  if (hasColumnConfig(columnKey)) {
    return TAS_PROGRAMS_COLUMN_MAPPING[columnKey] as ColumnConfig;
  }
  if (isAggregationColumn(columnKey)) {
    return AGGREGATION_COLUMNS[columnKey] as ColumnConfig;
  }
  return undefined;
}
